import React, { useEffect, useRef, useState, useCallback } from 'react';

import keys from '../../utils/keys';
import useFetch from '../../react-hooks/use-fetch';
import { stringifyUrl } from '../../utils/url';
import Alert from '../alert';
import Loading from '../loading';
import { createFocusTrap } from 'focus-trap';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';

function ProjectFilters({
  facetsUrl,
  browseTitle,
  clearFilters,
  searchTitle,
  searchBtnLabel,
  mobileHideFilters,
  mobileShowFilters,
  totalResults,
  loadingDataLabel,
  errorHeading,
  errorDescription,
  setSearchQs,
  setFacetsQS,
  setFacetsLoaded,
  facetsInitial
}) {
  const [searchValue, setSearchValue] = useState('');
  const [selectedFacets, setSelectedFacets] = useState(
    facetsInitial ? facetsInitial.split('$') : []
  );
  const [updateFacets, setUpdateFacets] = useState(false);
  const [scroll, setScroll] = useState(null);
  const projectBox = useRef(null);
  const trigger = useRef(null);
  const sentinelRef = useRef(null);
  const [searched, setSearched] = useState(false);
  const [forceHide, setForceHide] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [visibility, setVisibility] = useState(false);
  const [hideTrigger, setHideTrigger] = useState(true);
  const [scrolling, setScrolling] = useState(false);
  const [focusTrap, setFocusTrap] = useState();
  const visibilityHidden = { visibility: 'hidden' };
  const visible = { visibility: 'visible' };

  const fetchFacets = stringifyUrl({
    url: facetsUrl
  });

  const { response, error, isLoading } = useFetch(fetchFacets);

  useEffect(() => {
    if (sentinelRef.current) {
      const observer = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setForceHide(true);
            } else {
              setForceHide(false);
            }
          });
        },
        {
          root: null,
          threshold: 0.1 // Adjust threshold as needed
        }
      );

      observer.observe(sentinelRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [sentinelRef.current]);

  useEffect(() => {
    if (visibility) {
      focusTrap.activate();
    }
  }, [visibility, focusTrap]);

  useEffect(() => {
    updateDimensions();

    document.body.style = 'overflow-x: visible';
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('scroll', handleTriggerPosition);
    window.addEventListener('scroll', handleScrollStop);

    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('scroll', handleTriggerPosition);
      window.removeEventListener('scroll', handleScrollStop);
    };
  }, [handleTriggerPosition]);

  useEffect(() => {
    if (projectBox.current) {
      setScroll(projectBox.current);

      const trap = createFocusTrap(projectBox.current, {
        escapeDeactivates: true,
        onActivate() {
          disableBodyScroll(projectBox.current);
        },
        onDeactivate() {
          clearAllBodyScrollLocks();
        }
      });

      setFocusTrap(trap);
    }
  }, [projectBox.current]);

  useEffect(() => {
    if (scroll) {
      scroll.addEventListener('transitionend', (e) => handleTransitionEnd(e));
    }
    return () => {
      if (scroll) {
        scroll.removeEventListener('transitionend', (e) =>
          handleTransitionEnd(e)
        );
      }
    };
  }, [scroll]);

  const updateDimensions = () => {
    setIsMobile(window.matchMedia('(max-width: 899px)').matches);
  };

  const handleFacetClick = (name) => {
    if (selectedFacets?.findIndex((facet) => facet === name) > -1) {
      setSelectedFacets((prev) => prev.filter((facet) => facet !== name));
    } else {
      setSelectedFacets((prev) => [...prev, name]);
    }
    setUpdateFacets(true);
  };

  useEffect(() => {
    if (updateFacets) {
      if (selectedFacets) {
        if (!isMobile) {
          setFacetsQS('facets', selectedFacets);
        }
      } else {
        setFacetsQS('facets', []);
      }
      setUpdateFacets(false);
    }
  }, [selectedFacets, isMobile, setFacetsQS, updateFacets]);

  useEffect(() => {
    if (response?.data) {
      setFacetsLoaded(true);
    }
  }, [isLoading, response, setFacetsLoaded]);

  const handleFacetClear = () => {
    setSelectedFacets((prev) => prev.filter((facet) => facet === ''));
    setUpdateFacets(true);
  };

  const handleSearchUpdate = (e) => {
    setSearchValue(e.currentTarget.value);
    setSearched(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.keyCode === keys.enter) {
      handleSearchSubmit();
    }
  };

  const handleSearchSubmit = () => {
    if (!searched) {
      if (searchValue) {
        setSearchQs(searchValue);
        setSearched(true);
      }
    } else {
      setSearched(false);
      setSearchValue('');
      setSearchQs('');
    }
  };

  const handleShowModal = () => {
    if (openModal) {
      setFacetsQS('facets', selectedFacets);
      focusTrap.deactivate();
    } else {
      setVisibility(true);
    }
    setOpenModal((prev) => !prev);
  };

  const handleTransitionEnd = (e) => {
    if (!e.currentTarget.classList.contains('project-filters__box--show')) {
      setVisibility(false);
    }
  };

  const handleTriggerPosition = useCallback(() => {
    setScrolling(true);
    setHideTrigger(true);

    const pos = trigger?.current?.getBoundingClientRect();
    if (pos) {
      if (pos.top < window.innerHeight - pos.height) {
        setHideTrigger(true);
      } else {
        if (!scrolling) {
          setHideTrigger(false);
        }
      }
    }
  }, [scrolling]);

  const handleScrollStop = () => {
    setHideTrigger(false);
    setScrolling(false);
  };

  if (isLoading) return <Loading label={loadingDataLabel} />;

  if (error) {
    return (
      <Alert
        heading={errorHeading}
        description={errorDescription}
        type="error"
      />
    );
  }

  const renderMobile = () => (
    <>
      <div
        ref={projectBox}
        style={visibility ? visible : visibilityHidden}
        className={`project-filters__box ${
          openModal ? 'project-filters__box--show' : ''
        }`}
      >
        <div className="project-filters__search">
          <label
            className="project-filters__search-label"
            htmlFor="search-input"
          >
            {searchTitle}
          </label>
          <div className="projec-filters__search-input-wrapper">
            <input
              className="project-filters__search-input"
              id="search-input"
              onKeyDown={(e) => handleSearchKeyDown(e)}
              onChange={(e) => handleSearchUpdate(e)}
            />
            <button
              className="project-filters__search-input-button"
              aria-live="polite"
              aria-label={searchBtnLabel}
              onClick={() => handleSearchSubmit()}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                role="presentation"
              >
                <use xlinkHref="#search-outline" />
              </svg>
            </button>
          </div>
        </div>
        <div className="project-filters__facets-wrapper">
          <div className="project-filters__facets-top">
            <h2 className="project-filters__facets-heading">{browseTitle}</h2>
          </div>
          <div className="project-filters__facets">
            {response?.data?.areasOfInterest?.map((area) => (
              <button
                className={
                  selectedFacets?.findIndex((facet) => facet === area) >= 0
                    ? 'project-filters__facet project-filters__facet--selected'
                    : 'project-filters__facet'
                }
                aria-pressed={
                  selectedFacets?.findIndex((facet) => facet === area) >= 0
                }
                key={area}
                onClick={() => handleFacetClick(area)}
              >
                {area}
              </button>
            ))}
          </div>
          {selectedFacets?.length > 0 && (
            <button
              className="project-filters__facets-clear-btn"
              onClick={() => handleFacetClear()}
            >
              {clearFilters}
            </button>
          )}
        </div>
        <button
          className={`project-filters__trigger  ${
            openModal ? 'project-filters__trigger--hide' : ''
          } ${hideTrigger ? 'project-filters__trigger--hidden' : ''}`}
          style={{
            display: !openModal ? 'none' : null
          }}
          onClick={() => handleShowModal()}
        >
          {openModal
            ? `${
              mobileHideFilters[0] ? mobileHideFilters[0] : ''
            } ${totalResults} ${
              mobileHideFilters[1] ? mobileHideFilters[1] : ''
            }`
            : mobileShowFilters}
        </button>
      </div>
      <button
        ref={trigger}
        className={`project-filters__trigger  ${
          openModal ? 'project-filters__trigger--hide' : ''
        } ${hideTrigger ? 'project-filters__trigger--hidden' : ''}
        ${forceHide ? 'project-filters__trigger--force-hide' : ''}`}
        style={{ display: openModal ? 'none' : null }}
        onClick={() => handleShowModal()}
      >
        {openModal
          ? `${
            mobileHideFilters[0] ? mobileHideFilters[0] : ''
          } ${totalResults} ${
            mobileHideFilters[1] ? mobileHideFilters[1] : ''
          }`
          : mobileShowFilters}
      </button>
      <div ref={sentinelRef} className="projec-filters__sentinel"></div>
    </>
  );

  const renderDesktop = () => (
    <div className="project-filters">
      <div className="project-filters__facets-wrapper">
        <div className="project-filters__facets-top">
          <h2 className="project-filters__facets-heading">{browseTitle}</h2>
          {selectedFacets?.length > 0 && (
            <button
              className="project-filters__facets-clear-btn"
              onClick={() => handleFacetClear()}
            >
              {clearFilters}
            </button>
          )}
        </div>
        <div className="project-filters__facets">
          {response?.data?.areasOfInterest?.map((area) => (
            <button
              className={
                selectedFacets?.findIndex((facet) => facet === area) >= 0
                  ? 'project-filters__facet project-filters__facet--selected'
                  : 'project-filters__facet'
              }
              aria-pressed={
                selectedFacets?.findIndex((facet) => facet === area) >= 0
              }
              key={area}
              onClick={() => handleFacetClick(area)}
            >
              {area}
            </button>
          ))}
        </div>
      </div>
      <div className="project-filters__search">
        <label className="project-filters__search-label" htmlFor="search-input">
          {searchTitle}
        </label>
        <div className="projec-filters__search-input-wrapper">
          <input
            className="project-filters__search-input"
            id="search-input"
            onKeyDown={(e) => handleSearchKeyDown(e)}
            onChange={(e) => handleSearchUpdate(e)}
            value={searchValue}
          />
          <button
            className="project-filters__search-input-button"
            aria-live="polite"
            aria-label={searchBtnLabel}
            onClick={() => handleSearchSubmit()}
          >
            {searched ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 20 20"
                role="presentation"
              >
                <use xlinkHref="#close-outline-light" />
              </svg>
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                role="presentation"
              >
                <use xlinkHref="#search-outline" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return isMobile ? renderMobile() : renderDesktop();
}

export default ProjectFilters;
